<script setup>
import { computed } from 'vue'
import { BadgeCheck, GraduationCap, Award } from 'lucide-vue-next'
import { subjectIcon } from '@/lib/subjectIcon'

/**
 * The block that opens a tutor profile: who they are and what they achieved.
 *
 * It follows the concept's ordering — face, name, university, degree, subjects,
 * then the two evidence panels — because that is the order a parent reads in.
 * The previous version opened with a decorative gradient band twice the height
 * of the content and led with a video count.
 *
 * Every panel disappears when its data is absent rather than showing a stand-in.
 * A profile with nothing recorded should look empty, because it is.
 */
const props = defineProps({
  tutor: { type: Object, required: true },
})

const initials = computed(() => {
  const parts = (props.tutor.name || '').split(' ').filter(Boolean)
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (parts[0]?.[0] || '?').toUpperCase()
})

const degrees = computed(() => props.tutor.academic_qualifications?.degrees ?? [])
const alevels = computed(() => props.tutor.academic_qualifications?.alevels ?? [])
const yearsOfExperience = computed(() => props.tutor.academic_qualifications?.years_of_experience)
const additional = computed(() => props.tutor.academic_qualifications?.additional_qualifications ?? [])

const primaryDegree = computed(() => degrees.value[0] ?? null)

/** "BSc Medicine" — the type and the subject, which is how a degree is named. */
const degreeName = (degree) =>
  [degree?.degree_type, degree?.title].filter(Boolean).join(' ') || null

/** "Imperial College London · BSc Medicine" — whatever of it exists. */
const degreeLine = computed(() => {
  const degree = primaryDegree.value
  if (!degree) return null

  return [degree.institution, degreeName(degree)].filter(Boolean).join(' · ')
})

/** "2021 - 2027", or one year alone, or nothing. */
const degreeYears = (degree) =>
  [degree?.start_year, degree?.end_year].filter(Boolean).join(' - ') || null

const isInProgress = (degree) => degree?.status === 'in_progress'

/**
 * A grade, only when it is one.
 *
 * `classification` holds the literal text "In Progress" for an unfinished
 * degree, which the profile printed as "Grade: In Progress" — a status wearing
 * the label of a result. An unfinished degree gets a badge instead, and the
 * grade line only appears when there is a grade to put in it.
 */
const degreeGrade = (degree) => {
  if (!degree?.classification || isInProgress(degree)) return null

  return degree.classification.trim().toLowerCase() === 'in progress'
    ? null
    : degree.classification
}
</script>

<template>
  <section class="surface-card overflow-hidden">
    <div class="flex flex-col gap-6 p-6 sm:flex-row sm:p-8">
      <!-- Portrait -->
      <div class="shrink-0">
        <img
          v-if="tutor.avatar"
          :src="tutor.avatar"
          :alt="tutor.name"
          class="size-32 rounded-xl border border-line object-cover sm:size-40"
        />
        <span
          v-else
          class="grid size-32 place-items-center rounded-xl bg-brand-soft font-display text-4xl font-bold text-brand sm:size-40"
          aria-hidden="true"
        >{{ initials }}</span>
      </div>

      <!-- Identity -->
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-3">
          <h1 class="font-display text-3xl font-bold leading-tight text-ink md:text-4xl">{{ tutor.name }}</h1>
          <span
            v-if="tutor.is_verified"
            class="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1 text-xs font-bold text-brand-soft-text"
          >
            <BadgeCheck class="size-4" /> Verified
          </span>
        </div>

        <p v-if="degreeLine" class="mt-2 text-lg font-semibold text-brand">{{ degreeLine }}</p>
        <p v-else-if="tutor.highlight" class="mt-2 text-lg text-muted">{{ tutor.highlight }}</p>

        <!-- Subjects -->
        <ul v-if="tutor.subjects?.length" class="mt-4 flex flex-wrap gap-x-5 gap-y-2">
          <li
            v-for="subject in tutor.subjects"
            :key="subject.id"
            class="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"
          >
            <span class="material-symbols-outlined text-lg text-brand" aria-hidden="true">{{ subjectIcon(subject) }}</span>
            {{ subject.name }}
          </li>
        </ul>

        <!-- Levels and exam boards -->
        <div v-if="tutor.levels?.length || tutor.examboards?.length" class="mt-4 flex flex-wrap gap-1.5">
          <span
            v-for="level in tutor.levels"
            :key="`level-${level.id}`"
            class="rounded-sm border border-line bg-surface-2 px-2.5 py-1 text-xs font-semibold text-muted"
          >{{ level.name }}</span>
          <span
            v-for="board in tutor.examboards"
            :key="`board-${board.id}`"
            class="rounded-sm border border-line px-2.5 py-1 text-xs font-semibold text-subtle"
          >{{ board.name }}</span>
        </div>
      </div>
    </div>

    <!-- Evidence -->
    <div
      v-if="primaryDegree || alevels.length"
      class="grid gap-px border-t border-line bg-line sm:grid-cols-2"
    >
      <!--
        Every degree, not the first with a count of the rest.
        The Qualifications tab is gone — it repeated what is here — so this panel
        has to be complete: the degree as it is named, where it was taken, the
        years, and whether it is finished.
      -->
      <div v-if="degrees.length" class="flex gap-4 bg-surface p-6">
        <span class="grid size-11 shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
          <GraduationCap class="size-5.5" />
        </span>
        <div class="min-w-0 space-y-4">
          <h2 class="font-sans text-sm font-bold uppercase tracking-wide text-subtle">
            {{ degrees.length > 1 ? 'Degrees' : 'Degree' }}
          </h2>
          <div v-for="(degree, index) in degrees" :key="index" class="min-w-0">
            <p v-if="degreeName(degree)" class="font-semibold text-ink">{{ degreeName(degree) }}</p>
            <p v-if="degree.institution" class="text-sm text-muted">{{ degree.institution }}</p>
            <p class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
              <span v-if="degreeYears(degree)">{{ degreeYears(degree) }}</span>
              <!--
                Brand, not warning. A degree in progress is an ordinary fact
                about a tutor still studying, and the amber this used to carry is
                the colour the site uses for "something is wrong" — a parent
                reading quickly saw a caution flag against the qualification.
              -->
              <span
                v-if="isInProgress(degree)"
                class="inline-flex items-center rounded-full bg-brand-soft px-2 py-0.5 text-xs font-bold text-brand-soft-text"
              >
                In Progress
              </span>
              <span v-if="degreeGrade(degree)">
                Grade: <strong class="text-ink">{{ degreeGrade(degree) }}</strong>
              </span>
            </p>
          </div>
        </div>
      </div>

      <div v-if="alevels.length" class="flex gap-4 bg-surface p-6">
        <span class="grid size-11 shrink-0 place-items-center rounded-full bg-brand-soft font-display text-base font-bold text-brand">
          A*
        </span>
        <div class="min-w-0">
          <h2 class="font-sans text-sm font-bold uppercase tracking-wide text-subtle">A-Level results</h2>
          <ul class="mt-2 flex flex-wrap gap-1.5">
            <li
              v-for="(alevel, index) in alevels"
              :key="index"
              class="inline-flex items-center gap-1.5 rounded-sm border border-line-brand bg-brand-soft px-2.5 py-1 text-xs font-semibold text-brand-soft-text"
            >
              <span>{{ alevel.subject }}</span>
              <!-- A separate element, not a leading space: HTML collapses the
                   space and the chip read "Computer ScienceA". -->
              <span v-if="alevel.grade" class="font-display text-sm">{{ alevel.grade }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Experience and extras -->
    <div
      v-if="yearsOfExperience || additional.length"
      class="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-line bg-surface-2 px-6 py-4 text-sm text-muted sm:px-8"
    >
      <span v-if="yearsOfExperience" class="inline-flex items-center gap-2">
        <Award class="size-4 text-brand" /> {{ yearsOfExperience }} of teaching experience
      </span>
      <span v-for="(item, index) in additional" :key="index" class="inline-flex items-center gap-2">
        <BadgeCheck class="size-4 text-brand" /> {{ item.name ?? item }}
      </span>
    </div>
  </section>
</template>
